import RealtimeKitClient, {
  type RTKClientOptions,
  type RTKParticipant,
  type RTKSelf,
} from '@cloudflare/realtimekit'

/**
 * RealtimeKit glue (PLAN-085 §5.7, D9). We use the **core** SDK and our own
 * components — the UI kit is ~28 MB unpacked and would decide the layout for
 * us. Everything RealtimeKit-shaped that the components need lives here.
 */

export type MeetingClient = RealtimeKitClient

/**
 * A subscription that survives a re-render: the SDK's emitters are plain
 * EventEmitters, so a component that forgets to unsubscribe leaks a closure
 * over stale state.
 */
export function onParticipantEvent(
  target: { on: (e: string, cb: () => void) => void; off?: (e: string, cb: () => void) => void },
  events: string[],
  handler: () => void
): () => void {
  for (const event of events) target.on(event, handler)
  return () => {
    for (const event of events) target.off?.(event, handler)
  }
}

/**
 * Attach a remote participant's audio to a hidden `<audio>` element.
 *
 * The core SDK does not play remote audio for you — the UI kit's
 * `RtkParticipantsAudio` is what normally does it, and we are not using the
 * UI kit. Without this, a meeting is silent (PLAN-085 §1.5).
 */
export function attachAudio(
  element: HTMLAudioElement,
  track: MediaStreamTrack | undefined | null
): void {
  if (!track) {
    element.srcObject = null
    return
  }
  const current = element.srcObject
  if (current instanceof MediaStream && current.getAudioTracks()[0]?.id === track.id) return
  element.srcObject = new MediaStream([track])
  // Autoplay can still be refused if the page has had no interaction; the
  // user reaches this page by clicking Join, so in practice it plays.
  void element.play().catch(() => {})
}

/** Attach a video track (self or remote) to a `<video>` element. */
export function attachVideo(
  element: HTMLVideoElement,
  track: MediaStreamTrack | undefined | null
): void {
  if (!track) {
    element.srcObject = null
    return
  }
  const current = element.srcObject
  if (current instanceof MediaStream && current.getVideoTracks()[0]?.id === track.id) return
  element.srcObject = new MediaStream([track])
  void element.play().catch(() => {})
}

export interface MediaDevices {
  audioInputs: MediaDeviceInfo[]
  videoInputs: MediaDeviceInfo[]
}

export function participantInitials(participant: { name?: string }): string {
  const name = participant.name?.trim()
  if (!name) return '?'
  const parts = name.split(/\s+/)
  return (parts.length > 1 ? `${parts[0]![0]}${parts[1]![0]}` : parts[0]!.slice(0, 2)).toUpperCase()
}

/** Everything a tile needs, flattened out of the SDK's mutable objects. */
export interface TileParticipant {
  id: string
  name: string
  /** HUB user id — RealtimeKit carries it as `custom_participant_id`. */
  userId: string | null
  audioEnabled: boolean
  videoEnabled: boolean
  screenShareEnabled: boolean
  videoTrack: MediaStreamTrack | null
  audioTrack: MediaStreamTrack | null
  screenShareTrack: MediaStreamTrack | null
  isSelf: boolean
}

export function toTile(participant: RTKParticipant): TileParticipant {
  return {
    id: participant.id,
    name: participant.name,
    userId: participant.customParticipantId ?? null,
    audioEnabled: participant.audioEnabled,
    videoEnabled: participant.videoEnabled,
    screenShareEnabled: participant.screenShareEnabled,
    videoTrack: participant.videoTrack ?? null,
    audioTrack: participant.audioTrack ?? null,
    screenShareTrack: participant.screenShareTracks?.video ?? null,
    isSelf: false,
  }
}

export function selfTile(self: RTKSelf): TileParticipant {
  return {
    id: self.id,
    name: self.name,
    userId: self.customParticipantId ?? null,
    audioEnabled: self.audioEnabled,
    videoEnabled: self.videoEnabled,
    screenShareEnabled: self.screenShareEnabled,
    videoTrack: self.videoTrack ?? null,
    audioTrack: null, // never play your own microphone back
    screenShareTrack: self.screenShareTracks?.video ?? null,
    isSelf: true,
  }
}

/**
 * Options for `initMeeting`. The SDK produces nothing until we ask it to:
 * the pre-join preview is our own `getUserMedia` stream (see
 * {@link openPreview}), and the user's choices are applied after `join()`.
 *
 * Touching `self.enableVideo()` while the SDK is still settling its own
 * defaults throws ERR1100 "Unsupported concurrent calls on method:
 * meeting.self.disableVideo" — keeping the two apart avoids the race
 * entirely instead of trying to win it.
 */
export function clientOptions(
  authToken: string,
  onError: (error: unknown) => void
): RTKClientOptions {
  return {
    authToken,
    defaults: { audio: false, video: false },
    onError,
  }
}

export interface Preview {
  stream: MediaStream
  videoTrack: MediaStreamTrack | null
  devices: MediaDevices
  /** Refused per kind — tape over the lens must not also mute you. */
  denied: { audio: boolean; video: boolean }
}

/**
 * The pre-join camera preview, independent of RealtimeKit. Asking for
 * permission here is also what makes `enumerateDevices` return real labels
 * rather than empty strings.
 */
export async function openPreview(): Promise<Preview> {
  const stream = new MediaStream()
  const denied = { audio: false, video: false }
  // Camera and microphone are requested **separately**: one refusal must not
  // cost the other (a denied camera used to arrive muted too), and asking for
  // both at once wedges some browsers.
  for (const kind of ['video', 'audio'] as const) {
    try {
      const granted = await navigator.mediaDevices.getUserMedia({ [kind]: true })
      for (const track of granted.getTracks()) stream.addTrack(track)
    } catch {
      denied[kind] = true
    }
  }
  const all = await navigator.mediaDevices.enumerateDevices().catch(() => [])
  return {
    stream,
    videoTrack: stream.getVideoTracks()[0] ?? null,
    devices: {
      audioInputs: all.filter((d) => d.kind === 'audioinput'),
      videoInputs: all.filter((d) => d.kind === 'videoinput'),
    },
    denied,
  }
}

/**
 * Re-open the preview's camera on another device (board m5gc #11), so the
 * picture on the pre-join screen is the camera that will be published.
 * The microphone is not previewed, so it needs no swap.
 */
export async function switchPreviewVideo(preview: Preview, deviceId: string): Promise<Preview> {
  let granted: MediaStream
  try {
    granted = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: { exact: deviceId } },
    })
  } catch {
    return preview
  }
  for (const track of preview.stream.getVideoTracks()) {
    track.stop()
    preview.stream.removeTrack(track)
  }
  const videoTrack = granted.getVideoTracks()[0] ?? null
  if (videoTrack) preview.stream.addTrack(videoTrack)
  return { ...preview, videoTrack }
}

/** Release the preview before RealtimeKit opens the same devices. */
export function closePreview(preview: Preview | null): void {
  for (const track of preview?.stream.getTracks() ?? []) track.stop()
}

/**
 * RealtimeKit refuses overlapping calls to the same self-media method
 * ("Unsupported concurrent calls on method: meeting.self.disableVideo"), and
 * a UI where a toggle can be clicked twice, or where StrictMode runs an
 * effect twice, produces exactly that. Every media call goes through one
 * queue so they are strictly sequential.
 */
export function createMediaQueue(): (op: () => Promise<void>) => Promise<boolean> {
  let tail: Promise<unknown> = Promise.resolve()
  return (op) => {
    const next = tail.then(op).then(
      () => true,
      (err) => {
        // Never rethrow: one refused device must not stall the queue behind
        // it. The caller decides what a false means (REVIEW-2 #5).
        console.error('[meet] media operation failed', err)
        return false
      }
    )
    tail = next
    return next
  }
}
